import React, { useEffect } from "react";

import { CodeMirrorViewContext, useCodeMirrorViewContext } from "./codemirror-view-context.js";
import { EditingContextProvider, useEditingContext } from "./contexts/editing-context.js";
import { SelectionContextProvider, useSelectionContext } from "./contexts/selection-context.js";
import { TableProvider } from "./contexts/table-context.js";
import { TabularProvider, useTabularContext } from "./contexts/tabular-context.js";
import { TableGeneratorHelpModal } from "./help-modal.js";
import { Table } from "./table.js";
import { Toolbar } from "./toolbar/toolbar.js";
import { ColumnWidthModal } from "./toolbar/column-width-modal/modal.js";

class TableGeneratorErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error) {
        console.error("table-generator render failed", error);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="table-generator table-generator-error-container">
                    <div className="notification notification-type-info" role="alert">
                        <div className="notification-icon">i</div>
                        <div className="notification-content-and-cta">
                            <div className="notification-content">
                                <p>
                                    <strong>This table can't be displayed right now.</strong>
                                </p>
                                <p>Some table elements are not supported by the visual renderer yet.</p>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

function TabularWrapper() {
    const { setSelection, selection } = useSelectionContext();
    const { commitCellData, cellData } = useEditingContext();
    const { ref } = useTabularContext();
    const view = useCodeMirrorViewContext();

    useEffect(() => {
        const listener = (event) => {
            if (
                !ref.current?.contains(event.target) &&
                !event.target.closest?.(".table-generator-help-modal") &&
                !event.target.closest?.(".table-generator-width-modal")
            ) {
                if (selection) {
                    setSelection(null);
                }
                if (cellData) {
                    commitCellData();
                }
            } else {
                view.dispatch({});
            }
        };
        window.addEventListener("mousedown", listener);
        return () => window.removeEventListener("mousedown", listener);
    }, [cellData, commitCellData, ref, selection, setSelection, view]);

    return (
        <div className="table-generator" ref={ref}>
            {!view.state.readOnly && <Toolbar />}
            <Table />
        </div>
    );
}

export function Tabular({ tabularNode, view, tableNode, parsedTableData, directTableChild }) {
    return (
        <TableGeneratorErrorBoundary>
            <CodeMirrorViewContext.Provider value={view}>
                <TabularProvider>
                    <TableProvider
                        tabularNode={tabularNode}
                        tableData={parsedTableData}
                        tableNode={tableNode}
                        directTableChild={directTableChild}
                        view={view}
                    >
                        <SelectionContextProvider>
                            <EditingContextProvider>
                                <TabularWrapper />
                            </EditingContextProvider>
                            <ColumnWidthModal />
                        </SelectionContextProvider>
                    </TableProvider>
                    <TableGeneratorHelpModal />
                </TabularProvider>
            </CodeMirrorViewContext.Provider>
        </TableGeneratorErrorBoundary>
    );
}
